import {BaSeInputList, BaSeOptionPicker,} from "@base/react"
import {useCallback, useEffect, useMemo, useState} from "react"
import {converterSnakeToCamelCase, eventTag, getCurrentCNPJ, uuid,} from "../../services/utils"
import PagePlanningsStructure, {TabsAndOrderBy,} from "../../components/page-plannings-structure"
import {useApp} from "../../hooks/use-app"
import moment from "moment/moment"
import Loading from "../../components/loading"
import EmptyPlannings from "../../components/empty-plannings"
import ListPlanning from "./components/listPlannings"
import ApiPlannings from "../../services/planning"
import {optionsPeriod} from "../../mocks/consts"
import {useDebounced} from "../../hooks/use-debounce"
import "./index.scss"

const apiPlannings = ApiPlannings()

const event = {
  name: "consulting",
  category: "consultoria",
  action: "click",
}

const PAGE_SIZE = 8 //8
const DEFAULT_ORDER_BY = "sort_by_created_at=desc"
const INITIAL_LIST_STATE = {
  count: 0,
  results: [],
  loading: false,
}
const INITIAL_FILTERS = {
  searchList: [],
  period: null,
  usersShared: [],
  companiesShared: [],
  isFinished: null,
  postCredit: false,
  orderBy: DEFAULT_ORDER_BY,
}
const ALL_USERS_SHARED_OPTION = {
  id: 0,
  label: "Todos",
}
const ALL_COMPANIES_OPTION = {
  id: 0,
  label: "Todas",
}

const Consulting = ({ account }) => {
  const {
    companies,
  } = useApp()

  const currentCnpj = useMemo(() => {
    return getCurrentCNPJ(companies) || null
  }, [companies])

  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState("")
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const [listPlanning, setListPlanning] = useState(INITIAL_LIST_STATE)

  const [openCardId, setOpenCardId] = useState(null)

  const [paramsPlanning, setParamsPlanning] = useState({
    cnpj: currentCnpj,
    page: 1,
    page_size: PAGE_SIZE,
  })
  const [optionsShared, setOptionsShared] = useState([])
  const [optionsCompanies, setOptionsCompanies] = useState([])
  const [usersSharedSelectedIds, setUsersSharedSelectedIds] = useState([
    ALL_USERS_SHARED_OPTION.id,
  ])
  const [companiesSharedSelectedIds, setCompaniesSharedSelectedIds] = useState([
    ALL_COMPANIES_OPTION.id,
  ])
  const optionsSharedWithAll = useMemo(() => {
    return [ALL_USERS_SHARED_OPTION, ...optionsShared]
  }, [optionsShared])
  const optionsCompaniesWithAll = useMemo(() => {
    return [ALL_COMPANIES_OPTION, ...optionsCompanies]
  }, [optionsCompanies])
  const searchParam = useMemo(() => {
    if (!filters.searchList.length) return null

    return filters.searchList
      .map((item) => item.label)
      .join(",")
  }, [filters.searchList])
  const periodParams = useMemo(() => {
    if (!filters.period?.monthAmt) {
      return {
        start_date: null,
        end_date: null,
      }
    }

    return {
      start_date: moment()
        .subtract(filters.period.monthAmt, "months")
        .format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
    }
  }, [filters.period])
  const usersSharedParam = useMemo(() => {
    if (!filters.usersShared.length) return null

    const ids = filters.usersShared
      .filter((item) => item.id !== 0)
      .map((item) => item.id)

    return ids.length ? ids.join(",") : null
  }, [filters.usersShared])
  const companiesSharedParam = useMemo(() => {
    if (!filters.companiesShared.length) return null

    const ids = filters.companiesShared
      .filter((item) => item.id !== 0)
      .map((item) => item.id)

    return ids.length ? ids.join(",") : null
  }, [filters.companiesShared])

  const resetPlanningPage = () => {
    setParamsPlanning((prev) => ({
      ...prev,
      page: 1,
    }))
  }
  const setPlanningLoading = (value = true) => {
    setListPlanning((prev) => ({
      ...prev,
      loading: value,
    }))
  }

  const getUsersShared = async () => {
    try {
      const { data } = await apiPlannings.usersSharedList({
        cnpj: currentCnpj,
        is_consultor: true,
      })
      const newData = data.map(item => {
        const id = item?.cpf.replace(/\D/g, "")
        return {
          id: id,
          label: item?.name,
          cpf: item?.cpf,
        }
      })
      setOptionsShared(newData)
    } catch (error) {
      console.warn(error)
    } finally {

    }
  }
  const getCompaniesShared = async () => {
    try {
      const { data } = await apiPlannings.companiesSharedList()
      const newData = data.map(item => {
        const id = item?.cnpj.replace(/\D/g, "")
        return {
          id: id,
          label: item?.name,
          cnpj: item?.cnpj,
        }
      })
      setOptionsCompanies(newData)
    } catch (error) {
      console.warn(error)
    } finally {

    }
  }

  const toggleSearchTags = (item) => {
    let nextTags = [...filters.searchList]

    if (typeof item !== "string") {
      nextTags = nextTags.filter((tag) => tag.id !== item.id)
    } else {
      const value = item.trim()

      if (!value) return

      nextTags.push({
        id: uuid(),
        label: value,
      })
    }

    setSearchInput("")

    setFilters((prev) => ({
      ...prev,
      searchList: nextTags,
    }))

    resetPlanningPage()
    setPlanningLoading(true)
  }
  const handlePeriodChange = (selected) => {
    const period = selected?.[0] || null

    setFilters((prev) => ({
      ...prev,
      period,
    }))

    resetPlanningPage()
    setPlanningLoading(true)

    eventTag(
      event.name,
      event.category,
      "submit",
      `Filtro por período: ${period?.label || "Todos"}`
    )
  }

  const [debouncedUsersShared] = useDebounced((selectedUsers) => {
    eventTag(
      event.name,
      event.category,
      "submit",
      `Filtro por compartilhamento`
    )
    setFilters((prev) => ({
      ...prev,
      usersShared: selectedUsers || [],
    }))

    resetPlanningPage()
    setPlanningLoading(true)
  }, 500)
  const [debouncedCompaniesShared] = useDebounced((selectedUsers) => {
    eventTag(
      event.name,
      event.category,
      "submit",
      `Filtro por empresa`
    )
    setFilters((prev) => ({
      ...prev,
      companiesShared: selectedUsers || [],
    }))

    resetPlanningPage()
    setPlanningLoading(true)
  }, 500)
  const handleUsersSharedChange = (selectedUsers) => {
    const selectedItems = selectedUsers || []
    const hasAllOption = selectedItems.some(
      (item) => item.id === ALL_USERS_SHARED_OPTION.id
    )
    const hadAllOptionSelected = usersSharedSelectedIds.includes(
      ALL_USERS_SHARED_OPTION.id
    )
    const normalizedUsers =
      hasAllOption && !hadAllOptionSelected
        ? []
        : selectedItems.filter((item) => item.id !== ALL_USERS_SHARED_OPTION.id)

    setUsersSharedSelectedIds(
      normalizedUsers.length
        ? normalizedUsers.map((item) => item.id)
        : [ALL_USERS_SHARED_OPTION.id]
    )
    debouncedUsersShared(normalizedUsers)
  }
  const handleCompaniesSharedChange = (selectedCompanies) => {
    const selectedItems = selectedCompanies || []
    const hasAllOption = selectedItems.some(
      (item) => item.id === ALL_COMPANIES_OPTION.id
    )
    const hadAllOptionSelected = companiesSharedSelectedIds.includes(
      ALL_COMPANIES_OPTION.id
    )
    const normalizedUsers =
      hasAllOption && !hadAllOptionSelected
        ? []
        : selectedItems.filter((item) => item.id !== ALL_COMPANIES_OPTION.id)

    setCompaniesSharedSelectedIds(
      normalizedUsers.length
        ? normalizedUsers.map((item) => item.id)
        : [ALL_COMPANIES_OPTION.id]
    )
    debouncedCompaniesShared(normalizedUsers)
  }

  const handleOrderByChange = useCallback((filter) => {
    const statusMap = {
      all: null,
      progress: false,
      completed: true,
    }
    const statusMapLabel = {
      all: "Todos",
      progress: "Em andamento",
      completed: "Concluídos",
    }

    setFilters((prev) => {
      const nextIsFinished = statusMap[filter.tab]
      const nextOrderBy = filter.orderBy || DEFAULT_ORDER_BY

      eventTag(
        event.name,
        event.category,
        "submit",
        `Filtro por: ${statusMapLabel[filter.tab]}`
      )

      if (
        prev.isFinished === nextIsFinished &&
        prev.orderBy === nextOrderBy
      ) {
        return prev
      }

      return {
        ...prev,
        isFinished: nextIsFinished,
        orderBy: nextOrderBy,
      }
    })

    setParamsPlanning((prev) => {
      if (prev.page === 1) return prev

      return {
        ...prev,
        page: 1,
      }
    })

    setPlanningLoading(true)
  }, [])

  const loadMore = () => {
    eventTag(
      "click-button",
      event.category,
      event.action,
      `Carregar mais planejamentos`
    )

    if (listPlanning.loading) return

    setPlanningLoading(true)
    setParamsPlanning((prev) => ({
      ...prev,
      page: prev.page + 1,
    }))
  }
  const handleSearchInputEnter = (event) => {
    event.preventDefault()
    event.currentTarget
      ?.closest(".BaSe--input-list-container")
      ?.querySelector("button")
      ?.click()
  }

  const layoutFilters = (
    <>
      <BaSeInputList
        value={searchInput}
        onChange={setSearchInput}
        placeholder="Pesquise por nome do planejamento"
        isDisabled={listPlanning.loading}
        buttonAction={toggleSearchTags}
        typeButton="base-icon"
        nameButton="search"
        valueButton=""
        onSpecificKeyDown={[
          {
            listenerKey: "Enter",
            callback: handleSearchInputEnter,
          },
        ]}
        listItems={{
          direction: "row",
          columnNum: 1,
          gap: "8px",
          valuesList: filters.searchList,
          listActions: [
            {
              size: "small",
              sizeIcon: "small",
              ActionisDisabled: false,
              nameIcon: "close",
              color: "#005EB8",
              listAction: toggleSearchTags,
            },
          ],
        }}
      />
      <div className="wrapper-select with-icon calendar">
        <BaSeOptionPicker
          label="Período"
          values={optionsPeriod}
          emptyValueLabel="Selecione"
          enableSelectAll={false}
          maxOptionsSelected={1}
          withFilterText={false}
          initialSelectedValuesId={[0]}
          onChange={handlePeriodChange}
          isDisabled={listPlanning.loading}
        />
      </div>
      <div className="wrapper-select with-icon users">
        <BaSeOptionPicker
          label="Compartilhamento"
          values={optionsSharedWithAll}
          emptyValueLabel="Todos"
          enableSelectAll={false}
          withFilterText={false}
          initialSelectedValuesId={usersSharedSelectedIds}
          onChange={handleUsersSharedChange}
          isDisabled={listPlanning.loading}
        />
      </div>
      <div className="wrapper-select with-icon users">
        <BaSeOptionPicker
          label="Empresa"
          values={optionsCompaniesWithAll}
          emptyValueLabel="Todas"
          enableSelectAll={false}
          withFilterText={false}
          initialSelectedValuesId={companiesSharedSelectedIds}
          onChange={handleCompaniesSharedChange}
          isDisabled={listPlanning.loading}
        />
      </div>
    </>
  )
  const layoutContent = (
    <>
      <TabsAndOrderBy
        tabs={[
          {
            label: "Todos",
            count: listPlanning?.count || 0,
            action: "all",
          },
          {
            label: "Em andamento",
            action: "progress",
          },
          {
            label: "Concluídos",
            action: "completed",
          },
        ].filter(item => filters.postCredit ? item?.action === "all" : item)}
        onFilter={handleOrderByChange}
        isDisabled={listPlanning.loading}
        forceChangeTab={filters.postCredit && 'all'}
      />
      <div className="buttons-tab">
        <button type="button"
                className={"buttons-tab__button "+(!filters?.postCredit ? 'active' : '')}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    postCredit: false,
                  }))
                  eventTag(
                    event.name,
                    event.category,
                    "submit",
                    `Filtro por: Planejamentos`
                  )
                }}>
          Planejamentos
        </button>
        <button type="button"
                className={"buttons-tab__button "+(filters?.postCredit ? 'active' : '')}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    isFinished: null,
                    postCredit: true,
                  }))
                  eventTag(
                    event.name,
                    event.category,
                    "submit",
                    `Filtro por: Monitoramentos`
                  )
                }}>
          Monitoramentos
        </button>
      </div>
      <div className="list-planning">
        {loading ? (
          <div className="wrapper-loading-small">
            <Loading small />
          </div>
        ) : !listPlanning.count ? (
          <EmptyPlannings />
        ) : (
          <>
            <div className="list-planning">
              <ListPlanning
                event={event}
                account={account}
                listPlanning={listPlanning}
                openCardId={openCardId}
                setOpenCardId={setOpenCardId}
                loadMore={() => loadMore()}
              />
            </div>
          </>
        )}
      </div>
    </>
  )

  useEffect(() => {
    let isMounted = true

    const fetchPlanning = async () => {
      const isLoadMore = paramsPlanning.page > 1

      setPlanningLoading(true)

      const apiParams = {
        ...paramsPlanning,
        ...periodParams,
        post_credit: false
      }

      if (searchParam) {
        apiParams.plan_title = searchParam
      }
      if (filters.isFinished !== null) {
        apiParams.is_finished = filters.isFinished
      }
      if (usersSharedParam) {
        apiParams.planners = usersSharedParam
      }
      if (companiesSharedParam) {
        apiParams.company = companiesSharedParam
      }
      if (filters.orderBy) {
        const [key, value] = filters.orderBy.split("=")
        apiParams[key] = value
      }
      if (filters.postCredit !== null) {
        apiParams.post_credit = filters.postCredit

        // if (filters.postCredit) {
        //   apiParams.is_finished = null
        // }
      }

      try {
        const { data } = await apiPlannings.consultingList(apiParams)

        if (!isMounted) return

        const results = data.results.map((item) =>
          converterSnakeToCamelCase(item)
        )
        setListPlanning((prev) => ({
          ...data,
          loading: false,
          results: isLoadMore
            ? [...(prev.results || []), ...results]
            : results,
        }))
      } catch (err) {
        console.warn(err)

        if (isMounted) {
          setPlanningLoading(false)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchPlanning()

    return () => {
      isMounted = false
    }
  }, [
    paramsPlanning,
    periodParams,
    searchParam,
    filters.isFinished,
    filters.orderBy,
    filters.postCredit,
    usersSharedParam,
    companiesSharedParam,
  ])

  useEffect(() => {
    setLoading(true)
    setSearchInput("")
    setFilters(INITIAL_FILTERS)
    setUsersSharedSelectedIds([ALL_USERS_SHARED_OPTION.id])
    setCompaniesSharedSelectedIds([ALL_COMPANIES_OPTION.id])
    setListPlanning(INITIAL_LIST_STATE)
    setParamsPlanning({
      cnpj: currentCnpj,
      page: 1,
      page_size: PAGE_SIZE,
    })

    getUsersShared()
    getCompaniesShared()

    // eslint-disable-next-line
  }, [currentCnpj])

  return (
    <div className="consulting">
      <div className="container">
        <PagePlanningsStructure
          title="Consultoria"
          filters={layoutFilters}
        >
          {layoutContent}
        </PagePlanningsStructure>
      </div>
    </div>
  )
}

export default Consulting
